//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import { useTranslation } from "react-i18next";
import arePropsEqual from "react-fast-compare";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  Collapse,
  useMediaQuery,
} from '@mui/material';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

//utils
import { countValuesInObj } from "../../../../../helpers/countValuesInObj";

//styles
import { useStyles } from "./styles";

//routes
import routes from "../../../../../navigation/routes";

//hooks
import { useCreatives } from "../../../../../store/admin/сreativesV2/useCreatives";
import { usePermissions } from "../../../../../store/common/configuration/usePermissions";
import { useFlatFee } from "../../../../../store/admin/flatFee/useFlatFee";

//selectors
// import {
//   selectFlatFeeFilterData,
//   selectFlatFeeListDataColumnsSort,
// } from "../../../../../store/admin/flatFee/selectors";

//context
import {
	GlobalContext,
	initialAdmFlatFeeFilter,
	OrderType
} from "../../../../../context/GlobalContext";

//constants
import { favoriteFilterKeys } from "../../constants";

//components
import MuiDrawerWrapper from "../../../../UI/MuiDrawerWrapper";
import Filter from "./components/Filter";
import SelectedFiltersWrapper from "./components/SelectedFiltersWrapper";
import SortMenu from "../../../../common/SortMenu";
import PermissionsWrapper from "../../../../common/PermissionsWrapper";
import VisibleFilter from "./components/VisibleFilter";
import { reportStorageProps } from "../../constants";
import MuiModal from "../../../../UI/MuiModal";
import CreateUpdateFlatFee from "../CreateUpdateFlatFee";
import Button from "../../../../UI/Buttons/Button";
import { IFlatFeeFilterDataType } from "../../types";
import DownloadIcon from "@mui/icons-material/Download";


interface IDrawerFilterProps {
  initialFilter: IFlatFeeFilterDataType;
  setUpdateFilter: React.Dispatch<React.SetStateAction<IFlatFeeFilterDataType>>;
	children?: React.ReactNode | null;
	order: OrderType;
	setOrder: (order: OrderType) => void;
	onGenerateReport: () => void;
	setGenerateReport: React.Dispatch<React.SetStateAction<boolean>>;
	onDownload: () => void;
}

const DrawerFilter: React.FC<IDrawerFilterProps> = ({
  initialFilter,
  setUpdateFilter,
  children = null,
  order,
  setOrder,
  onGenerateReport,
  setGenerateReport,
  onDownload,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useNavigate();

  const { hasPermissions } = usePermissions();
  const context = useContext(GlobalContext);

  const mobile = useMediaQuery('(max-width:767px)');

	const { createFlatFee } = useFlatFee();

  // const dataFilters = useSelector(selectFlatFeeFilterData);
  // const sortingColumns = useSelector(selectFlatFeeListDataColumnsSort);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [activeField, setActiveField] = useState('');
	const [showModalCreate, setShowModalCreate] = useState(false);

  //favorite fields state
  const [favAdmFFStatuses, setFavAdmFFStatuses] = useLocalStorage<boolean>(favoriteFilterKeys.statuses, false);
  const [favAdmFFAffiliateIds, setFavAdmFFAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
  const [favAdmFFTrackerIds, setFavAdmFFTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
  const [favAdmFFBrandCodes, setFavAdmFFBrandCodes] = useLocalStorage<boolean>(favoriteFilterKeys.brandCodes, false);
  const [favAdmFFBrandCurrencies, setFavAdmFFBrandCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.brandCurrencies, false);

  const hasFavoriteFields = favAdmFFStatuses || favAdmFFAffiliateIds || favAdmFFTrackerIds || favAdmFFBrandCodes || favAdmFFBrandCurrencies;

  // useEffect(() => {
  //   if (!dataFilters) {
  //     getFilter()
  //   }
  // }, [dataFilters]);

  useEffect(() => {
    if (arePropsEqual(initialAdmFlatFeeFilter, initialFilter)) {
      setOpenCollapse(false)
    }
  }, [initialFilter]);

  const handleClearFilters = useCallback(() => {
    setUpdateFilter(initialAdmFlatFeeFilter);
  }, [setUpdateFilter]);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  }

  const handleSetActiveField = (field: string) => {
    setActiveField(field);
    setOpenDrawer(true);
  }

  const handleToggleCollapse = () => {
    setOpenCollapse(prevState => !prevState);
  }

  const onDeleteOption = useCallback((id: string | number, fieldName: string) => {
    setUpdateFilter( (prevState) => {
	    const newFieldData = reportStorageProps.exceptionsStr.includes(fieldName)
		    // @ts-ignore
		    ? prevState[fieldName].filter((_, i) => i !== indexToRemove)
		    // @ts-ignore
		    : prevState[fieldName].filter((item: any) => item.id !== id);

      context.setAdmFlatFeeFilter({
        ...context.admFlatFeeFilter,
        [fieldName]: newFieldData,
      });

      return {
        ...prevState,
        [fieldName]: newFieldData,
      }
    });
  }, [setUpdateFilter]);

  const collapseButton = (<>
    <Button
      type="button"
      variant="text"
      color="secondary"
      size="small"
      onClick={handleToggleCollapse}
      startIcon={openCollapse ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    >
      {openCollapse
        ? t("common.components.filters.hide")
        : t("common.components.filters.show")}
    </Button>
  </>);

	const handleOpenCreateFlatFee = useCallback(() => {
		setShowModalCreate(true);
	}, [setShowModalCreate]);

  return (<>
    {/*{!!dataFilters && (<>*/}
      <Box className={styles.block}>
        {!mobile && (
          <Box className={styles.first}>
            { !!children && children }
          </Box>
        )}

        <Box className={styles.second}>
          <Box className={styles.actions}>
            {/*reset filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleClearFilters}
              disabled={arePropsEqual(initialAdmFlatFeeFilter, initialFilter)}
            >
              <Tooltip title={t("common.buttons.reset_filters")} placement="bottom">
                <FilterAltOffIcon
                  color={arePropsEqual(initialAdmFlatFeeFilter, initialFilter) ? 'inherit' : 'primary'}
                />
              </Tooltip>
            </IconButton>

            {/*open filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleOpenDrawer}
            >
              <Tooltip title={t("common.components.filters.title")} placement="bottom">
                <Badge
                  className={styles.badge}
                  badgeContent={countValuesInObj(initialFilter)}
                  color="secondary"
                >
                  <FilterAltIcon
                    color={!!countValuesInObj(initialFilter) ? 'primary' : 'inherit'}
                  />
                </Badge>
              </Tooltip>
            </IconButton>

            {/*{!!sortingColumns && !showOrderOnlyMobile && mobile && <SortMenu*/}
            {/*  color="default"*/}
            {/*  columns={sortingColumns}*/}
            {/*  order={order}*/}
            {/*  setOrder={setOrder}*/}
            {/*  noMargin={true}*/}
            {/*  prefix="columns_for_display.table.flatFee.columns."*/}
            {/*/>}*/}
	          
	          {/*download*/}
	          <PermissionsWrapper
		          permissions={["api3.admin.flatfeecontroller.download"]}
	          >
		          <IconButton
			          className={styles.iconButton}
			          size={mobile ? 'small' : 'medium'}
			          onClick={onDownload}
		          >
			          <Tooltip title={t("common.buttons.download_report")} placement="bottom">
				          <DownloadIcon />
			          </Tooltip>
		          </IconButton>
	          </PermissionsWrapper>
          </Box>
        </Box>
      </Box>

      {!mobile && (
        <Box className={styles.buttonWrapper}>
          <PermissionsWrapper permissions={["api3.admin.flatfeecontroller.create"]}>
            <Button
              gradient
              sx={{ width: 'max-content' }}
              variant="contained"
              size={mobile ? 'small' : 'medium'}
              onClick={handleOpenCreateFlatFee}
            >
              {t("admin.flatFee.create.title")}
            </Button>
          </PermissionsWrapper>

          {(hasFavoriteFields || !!countValuesInObj(initialFilter)) && collapseButton}
        </Box>
      )}

      {!mobile && (
        <Collapse in={openCollapse} collapsedSize={0}>
          <VisibleFilter
            filter={initialFilter}
            setUpdateFilter={setUpdateFilter}
          />

          {!!countValuesInObj(initialFilter) && (
            <SelectedFiltersWrapper
              initialFilter={initialFilter}
              setActiveField={handleSetActiveField}
              onDeleteOption={onDeleteOption}
            />
          )}
        </Collapse>
      )}

      <MuiDrawerWrapper
        title={t("common.components.filters.title")}
        open={openDrawer}
        toggleDrawerState={setOpenDrawer}
      >
        <Filter
          activeField={activeField}
          setActiveField={setActiveField}
          initialFilter={initialFilter}
          setUpdateFilter={setUpdateFilter}
          setToggleDrawer={setOpenDrawer}
        />
      </MuiDrawerWrapper>
    {/*</>)}*/}
	  <MuiModal
		  width={496}
		  open={showModalCreate}
		  closeOnOutside={false}
		  setToggleModal={setShowModalCreate}
	  >
		  <CreateUpdateFlatFee
			  handleSubmitForm={createFlatFee}
			  closeModal={setShowModalCreate}
			  title={"admin.flatFee.createUpdateForm.title"}
		  />
	  </MuiModal>
  </>);
};

export default DrawerFilter;
